import { ConfirmationModal } from '@/components'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'

interface RoleRestrictionsModalProps {
  open: boolean
  onClose: () => void
  onAddAttorneys: () => void
  loading?: boolean
  hasError?: boolean
  errorMessage?: string
  isLoading?: boolean
  onRetry: () => void
  isRestrictedUser: boolean
}

const RoleRestrictionsModal = ({
  open,
  onClose,
  onAddAttorneys,
  loading = false,
  hasError = false,
  errorMessage,
  isLoading = false,
  onRetry,
  isRestrictedUser,
}: RoleRestrictionsModalProps) => {
  if (isLoading) {
    return (
      <ConfirmationModal
        open={open}
        title="Checking Permissions"
        description="Verifying your organization's attorney status. Please wait..."
        onClose={() => {}}
        AcceptButtonProps={{
          children: 'Please Wait',
          disabled: true,
          onClick: () => {},
        }}
        CancelButtonProps={{
          style: { display: 'none' },
        }}
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '444px',
          },
        }}
      />
    )
  }

  if (hasError) {
    return (
      <ConfirmationModal
        open={open}
        title="Cannot Verify Permissions"
        description={
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Unable to verify your organization&apos;s compliance status. This
              action is blocked for security reasons.
            </Typography>
            {errorMessage && (
              <Typography
                variant="body2"
                color="error.main"
                sx={{ fontSize: '0.875rem' }}
              >
                Technical details: {errorMessage}
              </Typography>
            )}
          </Box>
        }
        onClose={onClose}
        AcceptButtonProps={{
          children: 'Retry Check',
          onClick: onRetry,
          disabled: loading,
        }}
        CancelButtonProps={{
          children: 'Close',
          onClick: onClose,
          disabled: loading,
        }}
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '500px',
          },
        }}
      />
    )
  }

  if (isRestrictedUser) {
    const description = (
      <span>
        We&apos;re validating your bar license to finish setting up your
        account. This usually takes less than 48 hours. If you still don&apos;t
        have access by then, contact{' '}
        <a href="mailto:support@attorneyshare.com">
          support@attorneyshare.com.
        </a>
      </span>
    )

    return (
      <ConfirmationModal
        open={open}
        title="Almost There!"
        description={description}
        onClose={onClose}
        AcceptButtonProps={{
          children: 'Close',
          onClick: onClose,
          disabled: loading,
          variant: 'text',
        }}
        CancelButtonProps={{
          style: { display: 'none' },
        }}
      />
    )
  }

  return (
    <ConfirmationModal
      open={open}
      title="Almost There!"
      description="Add an attorney in Firm Settings so you can start sharing proposals, cases, or messages."
      onClose={onClose}
      AcceptButtonProps={{
        children: 'Add Attorneys',
        onClick: onAddAttorneys,
        disabled: loading,
      }}
      CancelButtonProps={{
        children: 'Close',
        onClick: onClose,
        disabled: loading,
      }}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '444px',
        },
      }}
    />
  )
}

export default RoleRestrictionsModal
